'use client';

import { Key } from 'react';
import {
  DallasTable,
  DallasTableHeaderCell,
  DallasTableHeaderRow,
  DallasTableRow,
} from '@ui/Dallas/Table/DallasTable';
import clsx from 'clsx';
import AddressSkeletonCell from '../../../../../../(authenticated)/(carrier)/(marketplace)/(loads)/loadboard/_ui/LoadTableSkeletonCells/AddressSkeletonCell';
import AgeSkeletonCell from '../../../../../../(authenticated)/(carrier)/(marketplace)/(loads)/loadboard/_ui/LoadTableSkeletonCells/AgeSkeletonCell';
import AppointmentSkeletonCell from '../../../../../../(authenticated)/(carrier)/(marketplace)/(loads)/loadboard/_ui/LoadTableSkeletonCells/AppointmentSkeletonCell';
import BrokerAndPickupSkeletonCell from '../../../../../../(authenticated)/(carrier)/(marketplace)/(loads)/loadboard/_ui/LoadTableSkeletonCells/BrokerAndPickupSkeletonCell';
import BrokerSkeletonCell from '../../../../../../(authenticated)/(carrier)/(marketplace)/(loads)/loadboard/_ui/LoadTableSkeletonCells/BrokerSkeletonCell';
import DimensionsSkeletonCell from '../../../../../../(authenticated)/(carrier)/(marketplace)/(loads)/loadboard/_ui/LoadTableSkeletonCells/DimensionsSkeletonCell';
import DistanceSkeletonCell from '../../../../../../(authenticated)/(carrier)/(marketplace)/(loads)/loadboard/_ui/LoadTableSkeletonCells/DistanceSkeletonCell';
import EquipmentCompactSkeletonCell from '../../../../../../(authenticated)/(carrier)/(marketplace)/(loads)/loadboard/_ui/LoadTableSkeletonCells/EquipmentCompactSkeletonCell';
import StopsSkeletonCell from '../../../../../../(authenticated)/(carrier)/(marketplace)/(loads)/loadboard/_ui/LoadTableSkeletonCells/StopsSkeletonCell';
import TrailerTypesSkeletonCell from '../../../../../../(authenticated)/(carrier)/(marketplace)/(loads)/loadboard/_ui/LoadTableSkeletonCells/TrailerTypesSkeletonCell';
import TripArrowWithExtraStopCountSkeletonCell from '../../../../../../(authenticated)/(carrier)/(marketplace)/(loads)/loadboard/_ui/LoadTableSkeletonCells/TripArrowWithExtraStopCountSkeletonCell';
import ViewPublicLoadSkeletonCell from './ViewPublicLoadSkeletonCell';

import styles from './PublicLoadTableSkeleton.module.scss';

export const DEFAULT_ROW_COUNT = 6;

function GET_SKELETON_KEY(index: number): Key {
  return index;
}

export interface Props {
  /**
   * Optional className
   * */
  className?: string;
  /**
   * Optional number of rows to be rendered in the skeleton
   * */
  rowCount?: number;
}

export default function PublicLoadTableSkeleton({
  className,
  rowCount = DEFAULT_ROW_COUNT,
}: Props) {
  const emptyRows = Array.from({ length: rowCount }).map((_, i) => i);

  return (
    <div className={clsx(className, styles.root)}>
      <DallasTable
        headerRow={
          <DallasTableHeaderRow>
            <DallasTableHeaderCell
              className={styles.ageColumn}
              columnName="Age"
            />
            <DallasTableHeaderCell
              className={styles.brokerColumn}
              columnName="Broker"
            />
            <DallasTableHeaderCell
              className={styles.brokerAndPickupColumn}
              columnName="Broker | Pickup"
            />
            <DallasTableHeaderCell
              className={styles.distanceColumn}
              columnName="Distance"
            />
            <DallasTableHeaderCell
              className={styles.pickupAppointmentColumn}
              columnName="Pickup"
            />
            <th className={styles.stopsColumn} />
            <th className={styles.pickupAddressColumn} />
            <th className={styles.tripArrowAndExtraStopsColumn} />
            <DallasTableHeaderCell
              className={styles.deliveryAddressColumn}
              columnName="Delivery"
            />
            <th className={styles.deliveryAppointmentNextToAddressColumn} />
            <DallasTableHeaderCell
              className={styles.deliveryAppointmentColumn}
              columnName="Delivery"
            />
            <DallasTableHeaderCell
              className={styles.equipmentCompactColumn}
              columnName="Equipment"
            />
            <DallasTableHeaderCell
              className={styles.trailerTypesColumn}
              columnName="Equipment"
            />
            <th className={styles.dimensionsColumn} />
            <th className={styles.callToActionColumn} />
            <th />
          </DallasTableHeaderRow>
        }
        rowTemplate={() => <LoadTableSkeletonRow />}
        data={emptyRows}
        getDataItemKey={GET_SKELETON_KEY}
      />
    </div>
  );
}

export function LoadTableSkeletonRow() {
  return (
    <DallasTableRow>
      <AgeSkeletonCell className={styles.ageColumn} />
      <BrokerSkeletonCell className={styles.brokerColumn} />
      <BrokerAndPickupSkeletonCell className={styles.brokerAndPickupColumn} />
      <DistanceSkeletonCell className={styles.distanceColumn} />
      <AppointmentSkeletonCell className={styles.pickupAppointmentColumn} />
      <AddressSkeletonCell className={styles.pickupAddressColumn} />
      <StopsSkeletonCell className={styles.stopsColumn} />
      <TripArrowWithExtraStopCountSkeletonCell
        className={styles.tripArrowAndExtraStopsColumn}
      />
      <AddressSkeletonCell className={styles.deliveryAddressColumn} />
      <AppointmentSkeletonCell
        className={styles.deliveryAppointmentNextToAddressColumn}
      />
      <AppointmentSkeletonCell className={styles.deliveryAppointmentColumn} />
      <EquipmentCompactSkeletonCell className={styles.equipmentCompactColumn} />
      <TrailerTypesSkeletonCell className={styles.trailerTypesColumn} />
      <DimensionsSkeletonCell className={styles.dimensionsColumn} />
      <ViewPublicLoadSkeletonCell className={styles.callToActionColumn} />
      <td />
    </DallasTableRow>
  );
}
